import React, { useState, useEffect } from 'react';
import { strukturOrganisasiService } from '../../../services/strukturOrganisasiService';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaSearch, FaImage, FaEye
} from 'react-icons/fa';

const ManajemenStrukturOrganisasi = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    const [formData, setFormData] = useState({
        judul: '',
        label: '',
        deskripsi: '',
        sumber: '',
        order: 0,
        status: 'publish',
    });

    const loadData = async () => {
        setLoading(true);
        const result = await strukturOrganisasiService.getStrukturList({ search: searchTerm });
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul: '',
            label: '',
            deskripsi: '',
            sumber: '',
            order: dataList.length + 1,
            status: 'publish',
        });
        setImageFile(null);
        setImagePreview(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul: item.judul || '',
            label: item.label || '',
            deskripsi: item.deskripsi || '',
            sumber: item.sumber || '',
            order: item.order || 0,
            status: item.status || 'publish',
        });
        setImageFile(null);
        setImagePreview(item.image_path ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${item.image_path}` : null);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!editing && !imageFile) {
            showNotif('Gambar wajib diupload', 'error');
            return;
        }

        setSubmitting(true);

        const submitData = new FormData();
        submitData.append('judul', formData.judul);
        submitData.append('label', formData.label || '');
        submitData.append('deskripsi', formData.deskripsi || '');
        submitData.append('sumber', formData.sumber || '');
        submitData.append('order', formData.order);
        submitData.append('status', formData.status);

        if (imageFile) {
            submitData.append('image', imageFile);
        }

        let result;
        if (editing) {
            result = await strukturOrganisasiService.updateStruktur(editing.id, submitData);
        } else {
            result = await strukturOrganisasiService.createStruktur(submitData);
        }

        if (result.success) {
            showNotif(result.data?.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await strukturOrganisasiService.deleteStruktur(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'publish' ? 'draft' : 'publish';
        const result = await strukturOrganisasiService.updateStatus(item.id, newStatus);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const filteredData = dataList.filter(item =>
        item.judul?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Struktur Organisasi</h2>
                    <p className="text-gray-600">Kelola bagan struktur organisasi</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                    <FaPlus className="mr-2" /> Tambah Struktur
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Search */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari struktur organisasi..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
            </div>

            {/* Grid View */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        Belum ada data struktur organisasi
                    </div>
                ) : (
                    filteredData.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow">
                            {/* Image Preview */}
                            <div className="relative h-48 bg-gray-100">
                                {item.image_path ? (
                                    <img
                                        src={getImageUrl(item.image_path)}
                                        alt={item.judul}
                                        className="w-full h-full object-contain p-2"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <FaImage className="text-4xl text-gray-300" />
                                    </div>
                                )}
                                <div className="absolute top-2 right-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'publish'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {item.status === 'publish' ? 'Publish' : 'Draft'}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-4">
                                <h3 className="font-bold text-[#0F4C3A] mb-1 line-clamp-1">{item.judul}</h3>
                                {item.label && (
                                    <p className="text-xs text-gray-500 mb-2">{item.label}</p>
                                )}
                                {item.sumber && (
                                    <p className="text-xs text-gray-400 mb-3 line-clamp-1">
                                        Sumber: {item.sumber}
                                    </p>
                                )}

                                {/* Actions */}
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <button
                                        onClick={() => handleToggleStatus(item)}
                                        className="text-xs text-gray-500 hover:text-gray-700"
                                    >
                                        {item.status === 'publish' ? 'Sembunyikan' : 'Publishkan'}
                                    </button>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => handleEdit(item)}
                                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                            title="Edit"
                                        >
                                            <FaEdit size={14} />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(item)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                            title="Hapus"
                                        >
                                            <FaTrash size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editing ? 'Edit Struktur Organisasi' : 'Tambah Struktur Organisasi'}
                                </h3>
                                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Judul <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: Bagan Struktur Organisasi BPKAD"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Label
                                        </label>
                                        <input
                                            type="text"
                                            name="label"
                                            value={formData.label}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Slide 1"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urutan
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Deskripsi
                                    </label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Deskripsi tambahan (opsional)"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Sumber
                                    </label>
                                    <input
                                        type="text"
                                        name="sumber"
                                        value={formData.sumber}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: Bagan Susunan Organisasi BPKAD (Update Juli 2026)"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Upload Gambar Bagan {!editing && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: JPG, PNG, GIF, SVG, WebP. Maks: 5MB
                                    </p>

                                    {imagePreview && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-xs text-gray-500 mb-2">Preview:</p>
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="max-h-48 object-contain mx-auto"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    >
                                        <option value="publish">Publish (Tampil di Website)</option>
                                        <option value="draft">Draft (Simpan Saja)</option>
                                        <option value="arsip">Arsip</option>
                                    </select>
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Mengupload...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Data?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.judul}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenStrukturOrganisasi;